import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {AlertTriangle, X} from "lucide-react";
import {getActionPresentation, getStatusPresentation} from "../statusMachine";
import {useTranslation, i18n} from "../../../shared/i18n";


const COPY = {
    request_documents: {
        get title() { return i18n.t("sections.onboarding.p177"); },
        get submit() { return i18n.t("sections.onboarding.p177"); },
        get description() { return i18n.t("sections.onboarding.p178"); },
    },
    reject: {
        get title() { return i18n.t("sections.onboarding.p179"); },
        get submit() { return i18n.t("sections.onboarding.p179"); },
        get description() { return i18n.t("sections.onboarding.p180"); },
    },
    publish: {
        get title() { return i18n.t("sections.onboarding.p181"); },
        get submit() { return i18n.t("sections.onboarding.p182"); },
        get description() { return i18n.t("sections.onboarding.p183"); },
    },
    rotate_access_token: {
        get title() { return i18n.t("sections.onboarding.p184"); },
        get submit() { return i18n.t("sections.onboarding.p184"); },
        get description() { return i18n.t("sections.onboarding.p185"); },
    },
    rotate_credentials: {
        get title() { return i18n.t("sections.onboarding.p186"); },
        get submit() { return i18n.t("sections.onboarding.p186"); },
        get description() { return i18n.t("sections.onboarding.p187"); },
    },
};

const focusableElements = (container) =>
    Array.from(
        container.querySelectorAll(
            "button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])",
        ),
    );

export const RequiredCommentDialog = ({
    open,
    action,
    applicationName,
    status,
    capturedRevision,
    currentRevision,
    pending,
    error,
    initialComment,
    onCancel,
    onConfirm,
}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const descriptionId = useId();
    const validationId = useId();
    const commentId = useId();
    const dialogRef = useRef(null);
    const textareaRef = useRef(null);
    const submitRef = useRef(null);
    const invokingElementRef = useRef(null);
    const [comment, setComment] = useState(initialComment || "");
    const [validationError, setValidationError] = useState("");
    // Синхронизация comment/validationError с пропсами выполняется во время рендера,
    // а не в useEffect: отложенный эффект после серверной 422-ошибки мог сработать
    // уже после того, как пользователь очистил поле, и вернуть старый текст поверх
    // нового ввода (гонка, ловившаяся тестами под нагрузкой). Паттерн
    // «derive state during render» из документации React.
    const [lastSync, setLastSync] = useState({open, action, initialComment});
    if (
        lastSync.open !== open ||
        lastSync.action !== action ||
        lastSync.initialComment !== initialComment
    ) {
        setLastSync({open, action, initialComment});
        if (open) {
            setComment(initialComment || "");
            setValidationError("");
        }
    }
    const copy = COPY[action];
    const requiresComment = ["request_documents", "reject", "publish"].includes(action);
    const actionPresentation = getActionPresentation(action);
    const currentStatus = getStatusPresentation(status);
    const targetStatus = actionPresentation?.resultStatus
        ? getStatusPresentation(actionPresentation.resultStatus)
        : null;
    const stale = capturedRevision !== currentRevision;

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() =>
            (requiresComment ? textareaRef.current : submitRef.current)?.focus(),
        );

        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [action, initialComment, open, requiresComment]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!pending) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open, pending]);

    if (!open || !copy || !actionPresentation) return null;

    const handleSubmit = (event) => {
        event.preventDefault();
        const normalized = comment.trim();
        if (requiresComment && (normalized.length < 1 || normalized.length > 2000)) {
            setValidationError(t("sections.onboarding.p188"));
            return;
        }
        if (!stale && !pending) onConfirm(requiresComment ? normalized : "");
    };

    const fieldErrors = error?.fieldErrors?.comment || [];
    const describedBy = [descriptionId, validationError || stale || error ? validationId : null]
        .filter(Boolean)
        .join(" ");

    return createPortal(
        <div
            className="onboarding-dialog-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !pending) onCancel();
            }}
        >
            <section
                ref={dialogRef}
                className="onboarding-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
            >
                <form className="onboarding-dialog-form" onSubmit={handleSubmit}>
                    <div className="onboarding-dialog-body">
                        <header className="onboarding-dialog-header">
                            <div>
                                <p>{t("sections.onboarding.p72")} {capturedRevision}</p>
                                <h2 id={titleId}>{copy.title}</h2>
                            </div>
                            <button
                                className="onboarding-icon-button"
                                type="button"
                                aria-label={t("sections.onboarding.p189")}
                                title={t("sections.onboarding.p189")}
                                disabled={pending}
                                onClick={onCancel}
                            >
                                <X aria-hidden="true" size={18} />
                            </button>
                        </header>

                        <p id={descriptionId} className="onboarding-dialog-description">
                            <strong>{applicationName}</strong>. {copy.description}
                        </p>

                        <div
                            className="onboarding-dialog-transition"
                            aria-label={t("sections.onboarding.p190")}
                        >
                            <span>{currentStatus.label}</span>
                            <span aria-hidden="true">→</span>
                            <strong>{targetStatus?.label || currentStatus.label}</strong>
                        </div>

                        {requiresComment ? (
                            <div className="onboarding-comment-field">
                                <label htmlFor={commentId}>{t("sections.onboarding.p191")}</label>
                                <textarea
                                    id={commentId}
                                    ref={textareaRef}
                                    value={comment}
                                    rows={6}
                                    disabled={pending}
                                    aria-invalid={Boolean(validationError || stale || error)}
                                    aria-describedby={describedBy}
                                    onChange={(event) => {
                                        setComment(event.target.value);
                                        setValidationError("");
                                    }}
                                />
                                <small>{comment.length} / 2000</small>
                            </div>
                        ) : null}

                        {stale || validationError || error ? (
                            <div id={validationId} className="onboarding-dialog-error" role="alert">
                                <AlertTriangle aria-hidden="true" size={16} />
                                <div>
                                    {stale ? (
                                        <p>{t("sections.onboarding.p192")}</p>
                                    ) : null}
                                    {validationError ? <p>{validationError}</p> : null}
                                    {!stale && error?.message ? <p>{error.message}</p> : null}
                                    {!stale
                                        ? fieldErrors.map((message) => <p key={message}>{message}</p>)
                                        : null}
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <footer className="onboarding-dialog-footer">
                        <button
                            className="admin-button admin-button-secondary"
                            type="button"
                            disabled={pending}
                            onClick={onCancel}
                        >
                            {t("sections.onboarding.p193")}
                        </button>
                        <button
                            ref={submitRef}
                            className={`admin-button onboarding-action-${actionPresentation.tone}`}
                            type="submit"
                            disabled={pending || stale}
                        >
                            {pending ? t("sections.onboarding.p118") : copy.submit}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
